import { accessLevelAtLeast, defaultAccessLevel, isOverridableRole, structuralCeiling, } from "./entity-roles.js";
/**
 * The effective access level a member holds for a feature: the per-member
 * override when one exists and is structurally legal, otherwise the Entity
 * Role default. This is the single function every Sealed-feature check must
 * call — do not read `entityRole` directly against the matrix elsewhere.
 */
export function effectiveAccessLevel(membership, feature) {
    const override = membership.overrides.get(feature);
    if (override === undefined) {
        return defaultAccessLevel(membership.entityRole, feature);
    }
    const ceiling = structuralCeiling(membership.entityRole, feature);
    return accessLevelAtLeast(ceiling, override) ? override : ceiling;
}
function hasAccess(membership, feature, required) {
    if (membership === null)
        return false;
    return accessLevelAtLeast(effectiveAccessLevel(membership, feature), required);
}
/** Any current member may read a Stable's own scoped data at VIEW level. */
export function canAccessStable(membership) {
    return membership !== null;
}
export function canViewStableFeature(membership, feature) {
    return hasAccess(membership, feature, "VIEW");
}
export function canUseStableFeature(membership, feature) {
    return hasAccess(membership, feature, "FULL");
}
export function canManageStableMembers(membership) {
    return canUseStableFeature(membership, "STAFF_RECORDS");
}
/**
 * Only Trainer/Co-Trainer may set a per-member permission override
 * (`docs/products/stable.md`: "A Trainer or Co-Trainer can override any
 * individual staff member's default"), and only for an overridable target
 * role, never for another Trainer/Co-Trainer.
 */
export function canOverrideMemberPermission(actorMembership, targetRole) {
    if (actorMembership === null)
        return false;
    if (actorMembership.entityRole !== "TRAINER" &&
        actorMembership.entityRole !== "CO_TRAINER") {
        return false;
    }
    return isOverridableRole(targetRole);
}
export function canEditHorseDetails(membership) {
    return canUseStableFeature(membership, "HORSE_DETAILS");
}
export function canViewHorseDetails(membership) {
    return canViewStableFeature(membership, "HORSE_DETAILS");
}
/**
 * Eligible Entity Owner tag holders per `docs/products/stable.md`: whoever
 * currently holds Trainer, Co-Trainer, or Racing Manager.
 */
export function isEligibleEntityOwner(role) {
    return (role === "TRAINER" || role === "CO_TRAINER" || role === "RACING_MANAGER");
}
export function canRequestEntityOwnerTransfer(actorMembership, currentOwnerId) {
    return (actorMembership !== null &&
        actorMembership.horsemanId === currentOwnerId &&
        isEligibleEntityOwner(actorMembership.entityRole));
}
/**
 * Link An Owner requires both the acting Trainer/Co-Trainer/Assistant
 * Trainer's Entity Role access (`LINK_AN_OWNER`) and the initiating
 * Stable's paid subscription (`docs/products/stable.md`: "Gated behind the
 * initiating Stable's paid subscription, deliberately, as a friction/
 * accountability layer against fraudulent or spammy link requests" —
 * `COMPANY.md` "Premium gates as friction, not just monetization"). Takes a
 * plain `"FREE" | "PAID"` rather than importing `StableSubscriptionTier`
 * from `contracts.ts` to keep this domain module free of a dependency on
 * the package's public contracts layer.
 */
export function canRequestOwnerLink(actorMembership, subscriptionTier) {
    return (canUseStableFeature(actorMembership, "LINK_AN_OWNER") &&
        subscriptionTier === "PAID");
}
/**
 * Stable Transfer initiation requires both the acting Trainer/Co-Trainer/
 * Assistant Trainer's Entity Role access (`STABLE_TRANSFER`) and the
 * initiating Stable's paid subscription (`docs/products/stable.md`: "the
 * initiating Stable must hold the paid subscription tier the feature
 * requires"), mirroring {@link canRequestOwnerLink} exactly.
 */
export function canRequestStableTransfer(actorMembership, subscriptionTier) {
    return (canUseStableFeature(actorMembership, "STABLE_TRANSFER") &&
        subscriptionTier === "PAID");
}
